/**
 * Teslyn Mobile's Android releases and updates (docs/specs/mobile/mobile-distribution.md; decision 519: Android is
 * handed out by Teslyn itself, no Play listing). Each channel (beta, stable: ADR-014, decision 698) has a small
 * manifest beside its APKs in the Cloudflare R2 bucket:
 *
 *   mobile/<channel>/latest.json                         this manifest
 *   mobile/<channel>/<version>/teslyn-mobile-<version>.apk  each release's APK
 *   mobile/<channel>/teslyn-mobile.apk                   a copy of the latest APK, the download page's fixed link
 *
 * `tools/release/publish-mobile.mjs` writes it; the phone reads it on start and once a day, offers a newer version,
 * and says "must update" below the channel's minimum. The numbers and names are in `mobile-release.json`, read by
 * the release tool too, so the two cannot drift.
 *
 * Beta and stable are two Android apps (GL-17, recommended answer built 2 Nov 2026): the beta is
 * `au.teslyn.mobile.beta` ("Teslyn Mobile (beta)", staging), the stable `au.teslyn.mobile` ("Teslyn Mobile",
 * production), so one phone can hold both. Each manifest names its channel's app. Beta builds from before the split
 * were `au.teslyn.mobile` too (`MOBILE_LEGACY_BETA_APP_ID`): `mobile-download` hands them a bridge manifest
 * (`mobileLegacyBetaRelease`) so their own updater installs the new beta app beside them, once.
 */
import rules from './mobile-release.json';
import { mobileVersionCode as versionCode } from './mobile-version.js';
import { compareReleaseVersions, parseReleaseVersion } from './release-feed.js';

export type MobileChannel = 'beta' | 'stable';
export const MOBILE_CHANNELS: readonly MobileChannel[] = ['beta', 'stable'];

/** The manifest's `v`. */
export const MOBILE_FEED_VERSION: number = rules.feed_version;
/** The Android package of each channel's Teslyn Mobile (the iPhone bundle is `au.teslyn.mobile` for both). */
export const MOBILE_APP_IDS: Readonly<Record<MobileChannel, string>> = rules.app_ids;
/** The Android package of the channel's app. */
export const mobileAppId = (channel: MobileChannel): string => MOBILE_APP_IDS[channel];
/** The name under the channel's icon. */
export const mobileAppName = (channel: MobileChannel): string => rules.app_names[channel];
/** The package every beta build had before the beta became its own app (up to 2026.10.16). */
export const MOBILE_LEGACY_BETA_APP_ID: string = rules.legacy_beta_app_id;
/** How often the phone looks for a new version while it has signal. */
export const MOBILE_UPDATE_CHECK_HOURS: number = rules.check_hours;
/** How soon a check that failed (no signal) is tried again. */
export const MOBILE_UPDATE_RETRY_HOURS: number = rules.retry_hours;
/** How long "Later" hides the notice of a newer version (decision 523's "dismissed for a day at a time"). */
export const MOBILE_UPDATE_LATER_HOURS: number = rules.later_hours;

/** The channel a Teslyn Cloud environment hands out: staging the beta, production the stable (decision 698). */
export function mobileChannelFor(environment: string): MobileChannel {
  return environment === 'production' ? 'stable' : 'beta';
}

/** Where a channel's files are, relative to the bucket (or the download address). */
export function mobileFeedPaths(
  channel: MobileChannel,
  version?: string,
): {
  readonly manifest: string;
  readonly latestApk: string;
  readonly apk: string | null;
  /** The release a tag built for this channel, not announced until it is promoted (stable). */
  readonly staged: string | null;
} {
  const dir = `${rules.folder}/${channel}`;
  return {
    manifest: `${dir}/${rules.manifest}`,
    latestApk: `${dir}/${rules.apk_latest}`,
    apk: version ? `${dir}/${mobileApkName(version)}` : null,
    staged: version ? `${dir}/${rules.staged}/${version}.json` : null,
  };
}

/** A release's APK, relative to its channel folder: `<version>/teslyn-mobile-<version>.apk`. */
export function mobileApkName(version: string): string {
  return `${version}/${rules.apk_prefix}${version}.apk`;
}

/** Android's version code of a release version (`mobile-version.ts`; 2026.10.14 is 202610014). */
export const mobileVersionCode: (version: string) => number | null = versionCode;

/**
 * Who may download Teslyn Mobile (decision 912): signed-in staff, that is anyone with a role in an organisation
 * (`membership.role`: every tenant role is a staff role), and pilots (R156, decision 917: Teslyn Mobile is a pilot's
 * whole account, so a pilot downloads it and its updates). Clients have no membership and are refused.
 */
export const MOBILE_DOWNLOAD_ROLES: readonly string[] = [
  'tenant_admin',
  'manager',
  'field_crew',
  'office_staff',
  'data_processor',
  'pilot',
];
export function mobileDownloadAllowed(roles: readonly string[]): boolean {
  return roles.some((r) => MOBILE_DOWNLOAD_ROLES.includes(r));
}

/** How long a download link the `mobile-download` function hands out works (seconds). */
export const MOBILE_LINK_SECONDS = 900;

/** One channel's manifest, `mobile/<channel>/latest.json`. */
export interface MobileRelease {
  readonly v: number;
  readonly app: string;
  readonly channel: MobileChannel;
  readonly version: string;
  readonly version_code: number;
  /** Below this version the phone must update; null when the channel has no minimum. */
  readonly min_version: string | null;
  /** The APK, relative to the manifest's folder (`mobileApkName`). */
  readonly apk: string;
  /** SHA-256 of the APK, lowercase hex. */
  readonly sha256: string;
  /** The APK's size in bytes. */
  readonly size: number;
  /** The release notes (content/release-notes/<version>.md without its front matter). */
  readonly notes: string;
  /** When it was published (ISO 8601). */
  readonly pub_date: string;
}

const SHA256 = /^[0-9a-f]{64}$/;
export const isSha256Hex = (text: string): boolean => SHA256.test(text);

/**
 * A manifest as read from the feed, or null with the reason when it cannot be trusted to install from: another app,
 * another channel, a version code that does not match its version, a minimum above the release, a damaged checksum or
 * an APK outside the channel folder.
 */
export function parseMobileRelease(
  json: unknown,
  channel: MobileChannel,
  /** The apps whose manifest is accepted: this channel's own (the phone); `mobile-download` also takes the old beta id. */
  apps: readonly string[] = [mobileAppId(channel)],
): { readonly release: MobileRelease } | { readonly problem: string } {
  const bad = (problem: string) => ({ problem });
  if (typeof json !== 'object' || json === null) return bad('not a manifest');
  const r = json as Record<string, unknown>;
  if (r.v !== MOBILE_FEED_VERSION)
    return bad(`manifest version ${String(r.v)} is not ${MOBILE_FEED_VERSION}`);
  if (typeof r.app !== 'string' || !apps.includes(r.app)) return bad(`the manifest is for ${String(r.app)}`);
  if (r.channel !== channel) return bad(`the manifest is for the ${String(r.channel)} channel`);
  const version = typeof r.version === 'string' ? r.version : '';
  const code = versionCode(version);
  if (code === null) return bad(`${version || 'no version'} is not a release version`);
  if (r.version_code !== code) return bad(`version code ${String(r.version_code)} does not match ${version}`);
  const min = r.min_version ?? null;
  if (min !== null) {
    if (typeof min !== 'string' || versionCode(min) === null)
      return bad(`minimum ${String(min)} is not a version`);
    if (compareReleaseVersions(min, version)! > 0)
      return bad(`minimum ${min} is above the release ${version}`);
  }
  if (r.apk !== mobileApkName(version))
    return bad(`the APK ${String(r.apk)} is not ${mobileApkName(version)}`);
  if (typeof r.sha256 !== 'string' || !isSha256Hex(r.sha256)) return bad('the checksum is not SHA-256');
  if (typeof r.size !== 'number' || !Number.isInteger(r.size) || r.size <= 0)
    return bad('the APK size is missing');
  return {
    release: {
      v: MOBILE_FEED_VERSION,
      app: r.app,
      channel,
      version,
      version_code: code,
      min_version: min,
      apk: r.apk,
      sha256: r.sha256,
      size: r.size,
      notes: typeof r.notes === 'string' ? r.notes : '',
      pub_date: typeof r.pub_date === 'string' ? r.pub_date : '',
    },
  };
}

/**
 * Who asked `mobile-download` for a channel's release, from the `app` the request names:
 * - `app`: this channel's own app (Teslyn Mobile from 2026.10.17, and Teslyn Cloud's page, which names it too);
 * - `legacy`: no `app` on the beta channel: a beta build from before the beta became its own app;
 * - `wrong`: another app, or no `app` on the stable channel (no stable build ever left it out).
 */
export type MobileDownloadCaller = 'app' | 'legacy' | 'wrong';
export function mobileDownloadCaller(app: unknown, channel: MobileChannel): MobileDownloadCaller {
  if (app === mobileAppId(channel)) return 'app';
  if ((app === undefined || app === null) && channel === 'beta') return 'legacy';
  return 'wrong';
}

/** What a beta build from before the split is told, in the release notes of its bridge manifest. */
export const MOBILE_BETA_MOVED_WORDS =
  'Teslyn Mobile (beta) is now its own app, so the beta and Teslyn Mobile can be on one phone. Install update ' +
  'adds "Teslyn Mobile (beta)" beside this app. Sign in there, then send any flights waiting here (Flights, Send ' +
  'now) and uninstall this one (hold its icon, Uninstall).';

/**
 * The beta release as a beta build from before the split (`au.teslyn.mobile`) accepts it: the same version, APK and
 * checksum under its own app id, with the words above as its notes. Its updater then offers "Update ready" and
 * Android installs the new beta app beside it (a different package installs as a new app). Null when the channel's
 * release is still an old-id build (nothing to bridge).
 */
export function mobileLegacyBetaRelease(release: MobileRelease): MobileRelease | null {
  if (release.channel !== 'beta' || release.app === MOBILE_LEGACY_BETA_APP_ID) return null;
  return { ...release, app: MOBILE_LEGACY_BETA_APP_ID, notes: MOBILE_BETA_MOVED_WORDS };
}

/** The APK's address: the manifest's folder and the release's `apk`. */
export function mobileApkUrl(manifestUrl: string, release: Pick<MobileRelease, 'apk'>): string {
  return manifestUrl.replace(/[^/]*$/, '') + release.apk;
}

/**
 * What this phone should do about the channel's latest release.
 * - `required`: it runs a version below the channel's minimum: "Update Teslyn Mobile" until it is updated.
 * - `available`: a newer version is out; the person may update now or later.
 * - `current`: it runs the latest release (or a newer build).
 * - `unknown`: no manifest yet (never checked, or the check failed) or a version that cannot be read.
 */
export type MobileUpdateState = 'required' | 'available' | 'current' | 'unknown';

export function mobileUpdateState(running: string, release: MobileRelease | null): MobileUpdateState {
  if (!release || !parseReleaseVersion(running)) return 'unknown';
  if (release.min_version && compareReleaseVersions(running, release.min_version)! < 0) return 'required';
  return compareReleaseVersions(release.version, running)! > 0 ? 'available' : 'current';
}

const HOUR = 3600_000;

/**
 * Whether the phone should read the manifest now: never read, a day since the last good read, or an hour since a
 * failed one (no signal at the airstrip). Times in milliseconds since the epoch.
 */
export function mobileUpdateCheckDue(
  last: { readonly checkedAt: number | null; readonly failedAt: number | null },
  now: number,
): boolean {
  if (last.failedAt !== null && (last.checkedAt === null || last.failedAt > last.checkedAt))
    return now - last.failedAt >= MOBILE_UPDATE_RETRY_HOURS * HOUR;
  if (last.checkedAt === null) return true;
  return now - last.checkedAt >= MOBILE_UPDATE_CHECK_HOURS * HOUR || now < last.checkedAt;
}

/**
 * Whether the notice shows: always when the phone must update; for a newer version unless "Later" was pressed for
 * that version within the last day.
 */
export function mobileUpdateNoticeShows(
  state: MobileUpdateState,
  version: string | null,
  later: { readonly version: string; readonly at: number } | null,
  now: number,
): boolean {
  if (state === 'required') return true;
  if (state !== 'available') return false;
  if (!later || later.version !== version) return true;
  return now - later.at >= MOBILE_UPDATE_LATER_HOURS * HOUR || now < later.at;
}
